import { Form } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import { admin_cse } from "@/config/roles";
import { Button, ButtonSizes, ButtonTypes, Checkbox, CustomForm } from "@/component-library";
import { Button2 } from "@/ui2/atoms/Button";
import { carePlanRequests } from "@/api/(requests)/carePlan";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import EditIcon from "@/assets/images/icons2/icon--edit.svg?component";
import { FormFieldError } from "@/component-library/FormFields/FormFieldError/FormFieldError";
import { FormTextArea } from "@/component-library/FormFields/FormTextArea/FormTextArea";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { isAuthorized } from "@/utils/authToken";
import { localTimestampFromStandardTimestamp } from "@/utils/momentUtils";
import { Modal2 } from "@/ui2/molecules/Modal";
import { ProviderEnum } from "@/core/constants/provider";
import { showToast } from "@/providers/ToastProvider";
import { toastMessageTypes } from "@/redux/actions/toaster";
import { XSS_REGEX } from "@/constants/security";

import "./MedicalCautions.styles.scss";

const DESCRIPTION_TEXT =
  "Medical conditions or surgeries that may impact long-term medication prescribing.";
const OTHER_OPTION_ID = "MC.OTHER";

export function MedicalCautions(props) {
  const profile = useSelector((state) => state.auth.profile);
  const { existingMedicalCautions, patientMedicalCautionOptions, refreshCautions } = props;
  const dispatch = useDispatch();

  const onModalSubmission = async (medicalCautions, otherFreeText, close) => {
    dispatch(showLoader());
    try {
      await carePlanRequests.update_patient_medical_cautions({
        context: {
          exceptionConfig: {
            CarePlanInvalidVersionException: {
              toastErrorType: toastMessageTypes.error_v2,
            },
          },
          showLoader: false,
        },
        data: {
          current_version: existingMedicalCautions.current_version,
          patient_medical_cautions: medicalCautions,
          patient_medical_cautions_other_free_text: otherFreeText,
        },
        urlParams: { patientId: props.patientId },
      });

      await refreshCautions();
      close();

      showToast([{ heading: "Medical cautions updated.", variant: "success" }]);
    } finally {
      dispatch(hideLoader());
    }
  };

  const renderExistingMedicalCautions = () => {
    const {
      patient_medical_cautions,
      patient_medical_cautions_other_free_text,
      updated_by_name,
      updated_timestamp,
    } = existingMedicalCautions;

    const formattedCautions = patient_medical_cautions.map((cautionId) => {
      const medicalCautionOption = patientMedicalCautionOptions.find(
        (option) => option.caution_id === cautionId,
      );

      const cautionText =
        medicalCautionOption.caution_id === OTHER_OPTION_ID
          ? patient_medical_cautions_other_free_text
          : medicalCautionOption.caution_text;

      return (
        <p className="PatientGoalsStrengthsCautions--readonly-text" key={cautionId}>
          - {cautionText}
        </p>
      );
    });

    return (
      <>
        <div
          className="tw:overflow-auto"
          {...getAutomationDataAttr("PatientMedicalCautions_existingCautions")}
        >
          <div className="tw:flex tw:flex-col tw:gap-2">{formattedCautions}</div>
        </div>

        <small className="tw:flex tw:justify-between tw:text-md tw:text-text-tertiary">
          <span>Last Updated by {updated_by_name}</span>

          <span>{localTimestampFromStandardTimestamp(updated_timestamp, "M/D/YYYY")}</span>
        </small>
      </>
    );
  };

  const renderNoMedicalCautionsSpecified = () => {
    const textBackgroundClass = `PatientGoalsStrengthsCautions--empty${
      isAuthorizedToEdit() ? "" : " no-permission"
    }`;

    return (
      <div {...getAutomationDataAttr("PatientMedicalCautions_emptyCautions")}>
        <div className={textBackgroundClass}>
          <p className="PatientGoalsStrengthsCautions--empty-text">No known medical cautions.</p>
        </div>
      </div>
    );
  };

  const isAuthorizedToEdit = () => {
    if (isAuthorized(admin_cse)) {
      return profile?.is_edit_care_plan_enabled;
    } else {
      return (
        props.isProviderOnCareTeam &&
        profile?.provider_type &&
        [ProviderEnum.PRESCRIBE].includes(profile.provider_type)
      );
    }
  };

  const editLabel = existingMedicalCautions?.patient_medical_cautions?.length ? "Edit" : "Add";

  return (
    <DashboardCard2
      Actions={
        isAuthorizedToEdit() && (
          <Modal2
            overlayProps={{ isDismissable: true }}
            title={`${editLabel} Medical Cautions`}
            trigger={
              <Button2
                icon={<EditIcon />}
                size="sm"
                variant="link"
                {...getAutomationDataAttr("PatientMedicalCautions_editButton")}
              >
                {editLabel}
              </Button2>
            }
          >
            {({ close }) => (
              <MedicalCautionsForm
                existingMedicalCautions={existingMedicalCautions}
                onClose={close}
                onModalSubmission={(cautions, otherText) =>
                  onModalSubmission(cautions, otherText, close)
                }
                patientMedicalCautionOptions={patientMedicalCautionOptions}
              />
            )}
          </Modal2>
        )
      }
      {...getAutomationDataAttr("PatientMedicalCautions_container")}
      classNameChildren="tw:max-h-80 tw:gap-3 tw:grid tw:grid-rows-[auto_1fr_auto] tw:h-full"
      title="Medical Cautions"
    >
      <small className="tw:text-sm tw:text-text-tertiary">{DESCRIPTION_TEXT}</small>

      {existingMedicalCautions?.patient_medical_cautions?.length
        ? renderExistingMedicalCautions()
        : renderNoMedicalCautionsSpecified()}
    </DashboardCard2>
  );
}

const MedicalCautionsForm = (props) => {
  const { existingMedicalCautions, patientMedicalCautionOptions } = props;
  const {
    patient_medical_cautions: initialPatientMedicalCautions,
    patient_medical_cautions_other_free_text: initialOtherFreeText,
  } = existingMedicalCautions;

  const maxOtherTextCharacters = 500;
  const medicalCautionsSchema = yup.object().shape({
    otherFreeText: yup.string().when("patientMedicalCautions", (cautions, schema) => {
      return cautions.includes(OTHER_OPTION_ID)
        ? yup
            .string()
            .required("Other reason must be specified")
            .max(maxOtherTextCharacters, `Limit text to ${maxOtherTextCharacters} characters`)
            .test("special characters", "Special characters are not allowed", (value) => {
              return !XSS_REGEX.test(value);
            })
        : schema;
    }),
    patientMedicalCautions: yup.array().of(yup.string()),
  });

  const checkboxHandler = async (formikProps, selectedOption) => {
    const updatedPatientMedicalCautions = new Set(formikProps.values.patientMedicalCautions);
    let updatedOtherFreeText = formikProps.values.otherFreeText;

    if (updatedPatientMedicalCautions.has(selectedOption.caution_id)) {
      updatedPatientMedicalCautions.delete(selectedOption.caution_id);

      // Reset the other text data
      if (selectedOption.caution_id === OTHER_OPTION_ID) {
        updatedOtherFreeText = "";
      }
    } else {
      updatedPatientMedicalCautions.add(selectedOption.caution_id);
    }

    await formikProps.setValues({
      otherFreeText: updatedOtherFreeText,
      patientMedicalCautions: Array.from(updatedPatientMedicalCautions),
    });
  };

  const renderOptions = (formikProps) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    return patientMedicalCautionOptions.map((option, index) => {
      return (
        <div key={index}>
          <Checkbox
            checked={formikProps.values.patientMedicalCautions.includes(option.caution_id)}
            label={option.caution_text}
            name="patientMedicalCautions"
            onClick={async () => checkboxHandler(formikProps, option)}
            testId={`PatientMedicalCautions_input_${option.caution_id}`}
            value={option.caution_id}
          />

          {option.caution_id === OTHER_OPTION_ID && (
            <>
              <FormTextArea
                {...formikInputProps}
                formikProps={formikProps}
                inputClassName="MedicalCautions--modal-free-text-input"
                name="otherFreeText"
                readOnly={!formikProps?.values?.patientMedicalCautions?.includes(OTHER_OPTION_ID)}
                testId="PatientMedicalCautions_otherTextInput"
                triggerErrorOnChange
              />

              <CharacterCounter
                characterLimit={maxOtherTextCharacters}
                className="PatientGoalsStrengthsCautions--input_character_counter"
                length={formikProps.values?.otherFreeText?.length}
              />
            </>
          )}
        </div>
      );
    });
  };

  const renderForm = (formikProps) => {
    return (
      <Form>
        <div className="PatientGoalsStrengthsCautions--modal_form">
          <div className="FormField-header_container" data-public>
            <p className="PatientGoalsStrengthsCautions--modal_subtitle">{DESCRIPTION_TEXT}</p>
          </div>

          <div className="MedicalCautions--modal-option-container">
            {renderOptions(formikProps)}
            {formikProps.errors?.patientMedicalCautions && (
              <FormFieldError>{formikProps.errors.patientMedicalCautions}</FormFieldError>
            )}
          </div>

          <div className="PatientGoalsStrengthsCautions-button_wrapper">
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryV2}
              type="submit"
            >
              Save
            </Button>
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryOutlineV2}
              onClick={props.onClose}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <CustomForm
      initialValues={{
        otherFreeText: initialOtherFreeText ?? "",
        patientMedicalCautions: [...initialPatientMedicalCautions],
      }}
      onSubmit={async (formData) => {
        await props.onModalSubmission(formData.patientMedicalCautions, formData.otherFreeText);
      }}
      render={renderForm}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={medicalCautionsSchema}
    />
  );
};
